import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BRAND_NAME, EMAIL_SUPPORT, EMAIL_CONTACT, PHONE_PRO } from "@/lib/tp13Config";
import Logo from "@/components/ui/Logo";
import BrandText from "@/components/ui/BrandText";
import CookieConsent from "@/components/seo/CookieConsent";
import LeadModal from "@/components/lead/LeadModal";

export default function PublicLayout() {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Scroll lock pour le menu mobile
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileMenu]);

  // Sticky CTA mobile + header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fermer le menu mobile au changement de route
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new Event('tp13:open-cookie-settings'));
  };

  // Pages où le sticky CTA ne doit pas apparaître
  const hideStickyCTAPages = [
    '/login',
    '/signup',
    '/reset',
    '/logout',
    '/dashboard',
    '/admin',
    '/merci',
    '/merci-ao',
    '/merci-commerce',
    '/mentions-legales',
    '/cgu',
    '/politique-confidentialite',
    '/cookies'
  ];
  const shouldShowStickyCTA = showStickyCTA && !hideStickyCTAPages.some(path => location.pathname.startsWith(path));
  const isAOPage = location.pathname.startsWith('/apporteurs-affaires');
  const isCommercePage = location.pathname.startsWith('/collaborateurs');
  const stickyCTA = isAOPage
    ? { to: '/signup?role=AO', label: "Devenir apporteur d'affaires" }
    : isCommercePage
      ? { to: '/signup?role=COMMERCE', label: 'Candidater comme restaurateur' }
      : { to: '/signup', label: 'Choisir mon profil' };

  // Vérifier si on est sur la page active
  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-tp13-black text-tp13-text flex flex-col">
      {/* Header */}
      <header className={`border-b border-tp13-border bg-tp13-surface sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-tp13-surface/95 shadow-lg shadow-black/20' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo + Brand */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <Logo size={56} />
            <BrandText />
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/vision"
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isActivePath('/vision')
                  ? 'text-tp13-red'
                  : 'text-tp13-text hover:text-tp13-red'
              }`}
            >
              Vision
            </Link>
            <Link
              to="/comment-ca-marche"
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isActivePath('/comment-ca-marche')
                  ? 'text-tp13-red'
                  : 'text-tp13-text hover:text-tp13-red'
              }`}
            >
              Comment ça marche
            </Link>
            <Link
              to="/faq"
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isActivePath('/faq')
                  ? 'text-tp13-red'
                  : 'text-tp13-text hover:text-tp13-red'
              }`}
            >
              FAQ
            </Link>
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="px-6 py-2.5 bg-tp13-red hover:bg-tp13-red2 text-white rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95"
              >
                Choisir mon profil
              </button>
              {showProfileMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowProfileMenu(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-64 bg-tp13-surface2 border border-tp13-border rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in-up">
                    <Link
                      to="/collaborateurs"
                      onClick={() => setShowProfileMenu(false)}
                      className="block px-4 py-3 hover:bg-tp13-surface transition-colors border-b border-tp13-border group"
                    >
                      <div className="font-medium group-hover:text-tp13-red transition-colors">Commerçants</div>
                      <div className="text-sm text-tp13-muted mt-1">
                        Marques virtuelles &amp; livraison
                      </div>
                    </Link>
                    <Link
                      to="/apporteurs-affaires"
                      onClick={() => setShowProfileMenu(false)}
                      className="block px-4 py-3 hover:bg-tp13-surface transition-colors group"
                    >
                      <div className="font-medium group-hover:text-tp13-red transition-colors">Apporteurs d'affaires</div>
                      <div className="text-sm text-tp13-muted mt-1">
                        Mise en relation &amp; commissions
                      </div>
                    </Link>
                  </div>
                </>
              )}
            </div>
            <Link
              to="/login"
              className={`px-6 py-2.5 border rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95 ${
                isActivePath('/login') 
                  ? 'border-tp13-red text-tp13-red' 
                  : 'border-tp13-border hover:border-tp13-red text-tp13-text'
              }`}
            >
              Connexion
            </Link>
            <Link
              to="/signup"
              className={`px-6 py-2.5 border rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95 ${
                isActivePath('/signup')
                  ? 'border-tp13-red bg-tp13-red text-white'
                  : 'border-tp13-red text-tp13-red hover:bg-tp13-red hover:text-white'
              }`}
            >
              Inscription
            </Link>
          </nav>

          {/* Burger Menu Mobile */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
            aria-label="Menu"
          >
            <span className={`w-6 h-0.5 bg-tp13-text transition-transform duration-300 ${showMobileMenu ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-tp13-text transition-opacity duration-300 ${showMobileMenu ? 'opacity-0' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-tp13-text transition-transform duration-300 ${showMobileMenu ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Menu Mobile Overlay */}
        {showMobileMenu && (
          <div className="fixed inset-0 bg-tp13-black z-40 md:hidden animate-fade-in">
            <div className="flex flex-col h-full animate-slide-in-right">
              {/* Header du menu mobile */}
              <div className="border-b border-tp13-border px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Logo size={40} />
                  <BrandText />
                </div>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-tp13-surface rounded-lg transition-colors"
                  aria-label="Fermer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Navigation mobile */}
              <nav className="flex-1 overflow-y-auto px-6 py-8">
                <div className="space-y-2">
                  <Link
                    to="/"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/') && location.pathname === '/'
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Accueil
                  </Link>
                  <Link
                    to="/vision"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/vision')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Vision
                  </Link>
                  <Link
                    to="/comment-ca-marche"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/comment-ca-marche')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Comment ça marche
                  </Link>
                  <Link
                    to="/faq"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/faq')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    FAQ
                  </Link>
                  
                  <div className="border-t border-tp13-border my-4 pt-4">
                    <div className="text-xs uppercase tracking-wider text-tp13-muted mb-2 px-4">Profils</div>
                  </div>
                  
                  <Link
                    to="/collaborateurs"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/collaborateurs')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Restaurateurs
                  </Link>
                  <Link
                    to="/apporteurs-affaires"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/apporteurs-affaires')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Apporteurs d'affaires
                  </Link>
                  
                  <div className="border-t border-tp13-border my-4 pt-4">
                    <div className="text-xs uppercase tracking-wider text-tp13-muted mb-2 px-4">Découvrir</div>
                  </div>
                  
                  <Link
                    to="/villes"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/villes')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Villes
                  </Link>
                  <Link
                    to="/blog"
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors ${
                      isActivePath('/blog')
                        ? 'bg-tp13-red text-white'
                        : 'hover:bg-tp13-surface'
                    }`}
                  >
                    Blog
                  </Link>
                  
                  <div className="border-t border-tp13-border my-6"></div>
                  
                  <Link
                    to="/login"
                    className="block px-4 py-3 text-lg border border-tp13-border hover:border-tp13-red rounded-lg transition-colors text-center"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-4 py-3 text-lg bg-tp13-red hover:bg-tp13-red2 text-white rounded-lg transition-colors text-center font-medium"
                  >
                    Candidater
                  </Link>
                </div>
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className={`flex-1 ${shouldShowStickyCTA ? 'pb-20 md:pb-0' : ''}`}>
        <Outlet />
      </main>

      {/* Sticky CTA Mobile */}
      {shouldShowStickyCTA && (
        <div className={`fixed bottom-0 left-0 right-0 z-40 md:hidden bg-tp13-surface border-t border-tp13-border shadow-lg transition-transform duration-500 ${
          showStickyCTA ? 'translate-y-0' : 'translate-y-full'
        }`}>
          <div className="px-6 py-3">
            <Link
              to={stickyCTA.to}
              className="block w-full px-6 py-3 bg-tp13-red hover:bg-tp13-red2 text-white rounded-lg font-medium transition-all duration-300 text-center cursor-pointer active:scale-95"
            >
              {stickyCTA.label}
            </Link>
            <p className="text-center text-xs text-tp13-muted mt-2">
              Gratuit • Sans engagement
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-tp13-border bg-tp13-surface mt-20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Top section - Brand + Description */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-12 pb-12 border-b border-tp13-border">
            <div className="max-w-md">
              <div className="mb-4 flex items-center gap-2">
                <Logo size={32} />
                <BrandText />
              </div>
              <p className="text-tp13-muted text-base leading-relaxed">
                Plateforme nationale de marques virtuelles pour la restauration et réseau d'apporteurs d'affaires.
              </p>
            </div>
            
            {/* CTA Footer */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup?role=COMMERCE"
                className="px-6 py-3 bg-tp13-red hover:bg-tp13-red2 text-white rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center hover:scale-105 active:scale-95"
              >
                Créer mon espace restaurateur
              </Link>
              <Link
                to="/signup?role=AO"
                className="px-6 py-3 border-2 border-tp13-border hover:border-tp13-red text-tp13-text hover:text-tp13-red rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center active:scale-95"
              >
                Créer mon espace apporteur
              </Link>
            </div>
          </div>

          {/* Bottom section - Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {/* Navigation */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Navigation</h3>
              <div className="space-y-3 text-sm">
                <Link
                  to="/vision"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Vision
                </Link>
                <Link
                  to="/comment-ca-marche"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Comment ça marche
                </Link>
                <Link
                  to="/faq"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  FAQ
                </Link>
                <Link
                  to="/villes"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Villes
                </Link>
                <Link
                  to="/departements"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Départements
                </Link>
                <Link
                  to="/regions"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Régions
                </Link>
                <Link
                  to="/blog"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Blog
                </Link>
              </div>
            </div>

            {/* Profils */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Profils</h3>
              <div className="space-y-3 text-sm">
                <Link
                  to="/collaborateurs"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Restaurateurs
                </Link>
                <Link
                  to="/apporteurs-affaires"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Apporteurs d'affaires
                </Link>
                <Link
                  to="/login"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Connexion
                </Link>
                <Link
                  to="/signup"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Inscription
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Contact</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-tp13-muted mb-1">Support</div>
                  <a
                    href={`mailto:${EMAIL_SUPPORT}`}
                    className="text-tp13-text hover:text-tp13-red transition-colors font-medium"
                  >
                    {EMAIL_SUPPORT}
                  </a>
                </div>
                <div>
                  <div className="text-tp13-muted mb-1">Commercial</div>
                  <a
                    href={`mailto:${EMAIL_CONTACT}`}
                    className="text-tp13-text hover:text-tp13-red transition-colors font-medium"
                  >
                    {EMAIL_CONTACT}
                  </a>
                </div>
                <div>
                  <div className="text-tp13-muted mb-1">Téléphone</div>
                  <a
                    href="tel:0261537228"
                    className="text-tp13-text hover:text-tp13-red transition-colors font-medium"
                  >
                    {PHONE_PRO}
                  </a>
                </div>
              </div>
            </div>

            {/* Légal */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Légal</h3>
              <div className="space-y-3 text-sm">
                <Link
                  to="/mentions-legales"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Mentions légales
                </Link>
                <Link
                  to="/cgu"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  CGU
                </Link>
                <Link
                  to="/politique-confidentialite"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Confidentialité
                </Link>
                <Link
                  to="/cookies"
                  className="block text-tp13-muted hover:text-tp13-red transition-colors"
                >
                  Cookies
                </Link>
                <button
                  onClick={handleOpenCookieSettings}
                  className="block text-tp13-muted hover:text-tp13-red transition-colors cursor-pointer text-left"
                >
                  Gérer mes cookies
                </button>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-tp13-border mt-12 pt-8 text-center text-sm text-tp13-muted">
            © {new Date().getFullYear()} {BRAND_NAME}. Tous droits réservés.
          </div>
        </div>
      </footer>

      {/* Cookie Consent */}
      <CookieConsent />

      {/* Lead Modal */}
      <LeadModal />
    </div>
  );
}
